/**
 * Sheet editor for trigger nodes in the DAG flow builder.
 * Allows configuring trigger type, scope, and schedule.
 */
import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useApp } from '@/contexts/AppContext';
import {
  BUILDABLE_TRIGGER_TYPES,
  getTriggerLabel,
  type TriggerTypeInfo,
} from '@/lib/automation-trigger-types';
import type { TriggerNodeConfig } from '@/lib/dag-types';

interface TriggerEditorSheetProps {
  open: boolean;
  onClose: () => void;
  config: TriggerNodeConfig;
  onSave: (config: TriggerNodeConfig) => void;
}

const CATEGORY_ORDER = ['system_health', 'battery', 'forecast', 'solar', 'usage', 'scheduled'] as const;
const CATEGORY_LABELS: Record<string, string> = {
  system_health: 'System Health',
  battery: 'Battery',
  forecast: 'Forecast',
  solar: 'Solar',
  usage: 'Usage',
  scheduled: 'Scheduled',
};

function groupByCategory(types: TriggerTypeInfo[]): Map<string, TriggerTypeInfo[]> {
  const map = new Map<string, TriggerTypeInfo[]>();
  for (const t of types) {
    const group = map.get(t.category) ?? [];
    group.push(t);
    map.set(t.category, group);
  }
  return map;
}

export function TriggerEditorSheet({ open, onClose, config, onSave }: TriggerEditorSheetProps) {
  const { properties } = useApp();
  const [triggerType, setTriggerType] = useState(config.trigger_type);
  const [scopeType, setScopeType] = useState<'all' | 'selected' | 'tagged'>(config.scope_type);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>(config.scope_property_ids ?? []);
  const [scopeTags, setScopeTags] = useState<string[]>(config.scope_tags ?? []);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (open) {
      setTriggerType(config.trigger_type);
      setScopeType(config.scope_type);
      setSelectedPropertyIds(config.scope_property_ids ?? []);
      setScopeTags(config.scope_tags ?? []);
      setTagInput('');
    }
  }, [open, config]);

  const grouped = groupByCategory(BUILDABLE_TRIGGER_TYPES);

  const handleSave = () => {
    onSave({
      ...config,
      title: getTriggerLabel(triggerType),
      trigger_type: triggerType,
      scope_type: scopeType,
      scope_property_ids: scopeType === 'selected' ? selectedPropertyIds : [],
      scope_tags: scopeType === 'tagged' ? scopeTags : [],
    });
    onClose();
  };

  const toggleProperty = (id: string) => {
    setSelectedPropertyIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !scopeTags.includes(tag)) {
      setScopeTags((prev) => [...prev, tag]);
    }
    setTagInput('');
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[400px] sm:max-w-[400px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Trigger</SheetTitle>
          <SheetDescription>Configure what starts this automation.</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Trigger Type */}
          <div className="space-y-2">
            <Label>Trigger Type</Label>
            <Select value={triggerType} onValueChange={setTriggerType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_ORDER.map((cat) => {
                  const items = grouped.get(cat);
                  if (!items?.length) return null;
                  return (
                    <div key={cat}>
                      <div className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {CATEGORY_LABELS[cat]}
                      </div>
                      {items.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </div>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Scope */}
          <div className="space-y-2">
            <Label>Scope</Label>
            <div className="flex gap-2">
              {(['all', 'selected', 'tagged'] as const).map((s) => (
                <Button
                  key={s}
                  variant={scopeType === s ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={() => setScopeType(s)}
                >
                  {s === 'all' ? 'All Properties' : s === 'selected' ? 'Selected' : 'Tagged'}
                </Button>
              ))}
            </div>

            {scopeType === 'selected' && (
              <div className="mt-2 max-h-40 overflow-y-auto rounded border border-border p-2 space-y-1">
                {properties.length === 0 && (
                  <p className="text-xs text-muted-foreground">No properties found.</p>
                )}
                {properties.map((p) => (
                  <label
                    key={p.id}
                    className="flex items-center gap-2 text-sm cursor-pointer hover:bg-wt-bg-tertiary/30 rounded px-1 py-0.5"
                  >
                    <input
                      type="checkbox"
                      checked={selectedPropertyIds.includes(p.id)}
                      onChange={() => toggleProperty(p.id)}
                      className="rounded border-border"
                    />
                    <span className="truncate">{p.name}</span>
                  </label>
                ))}
              </div>
            )}

            {scopeType === 'tagged' && (
              <div className="mt-2 space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add tag..."
                    className="flex-1 rounded border border-border bg-background px-2 py-1 text-sm"
                  />
                  <Button size="sm" variant="outline" onClick={addTag}>
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {scopeTags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="gap-1">
                      {tag}
                      <button
                        type="button"
                        onClick={() => setScopeTags((prev) => prev.filter((t) => t !== tag))}
                        className="ml-0.5 text-xs hover:text-destructive"
                      >
                        x
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Schedule (deferred) */}
          <div className="space-y-2">
            <Label>Schedule</Label>
            <p className="text-xs text-muted-foreground">Always active. Scheduled triggers coming soon.</p>
          </div>
        </div>

        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Trigger</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
